import { LegacyFieldBag } from '../../models/legacy-field-bag.model';
import {
  PersistedCard,
  PersistedCategory,
  PersistedLadderState,
  PersistedUiFilterState,
  PersistedUiState,
} from '../schemas/persisted-entities.model';
import { PersistedAppState } from '../schemas/persisted-app-state.model';

const APP_STATE_KEYS = ['schemaVersion', 'savedAt', 'categories', 'ladder', 'cards', 'ui', 'legacy'] as const;
const CATEGORY_KEYS = ['id', 'name', 'color', 'isSystem', 'legacy'] as const;
const CARD_KEYS = [
  'id',
  'title',
  'description',
  'notes',
  'categoryId',
  'status',
  'showInLadder',
  'priority',
  'importance',
  'parentCardId',
  'createdAt',
  'updatedAt',
  'deadline',
  'manualProgress',
  'ladderGroupColor',
  'legacy',
] as const;
const LADDER_KEYS = ['levels', 'legacy'] as const;
const UI_STATE_KEYS = ['ladder', 'allCards', 'legacy'] as const;
const UI_FILTER_KEYS = ['textQuery', 'status', 'categoryId', 'quickFilter', 'legacy'] as const;

export function preserveLegacyPersistedAppState<TState extends PersistedAppState>(
  persistedState: TState,
): TState {
  const uiStateRecord = toRecord(persistedState.ui);
  const preservedUiState = withLegacy(persistedState.ui, UI_STATE_KEYS);

  return {
    ...withLegacy(persistedState, APP_STATE_KEYS),
    categories: persistedState.categories.map((category) => withLegacy(category, CATEGORY_KEYS)),
    cards: persistedState.cards.map((card) => withLegacy(card, CARD_KEYS)),
    ladder: withLegacy(persistedState.ladder, LADDER_KEYS),
    ui: {
      ...preservedUiState,
      ladder: withLegacy(toPersistedUiFilterState(uiStateRecord['ladder']), UI_FILTER_KEYS),
      allCards: withLegacy(toPersistedUiFilterState(uiStateRecord['allCards']), UI_FILTER_KEYS),
    },
  } as unknown as TState;
}

function withLegacy<TValue extends object>(
  value: TValue,
  knownKeys: readonly string[],
): TValue & { legacy?: LegacyFieldBag } {
  const valueRecord = toRecord(value);
  const existingLegacy = normalizeLegacyBag(valueRecord['legacy']);
  const extractedLegacy = extractUnknownFields(valueRecord, knownKeys);
  const mergedLegacy = mergeLegacyBags(existingLegacy, extractedLegacy);

  if (!mergedLegacy) {
    return value;
  }

  return {
    ...valueRecord,
    legacy: mergedLegacy,
  } as TValue & { legacy?: LegacyFieldBag };
}

function extractUnknownFields(
  value: Record<string, unknown>,
  knownKeys: readonly string[],
): LegacyFieldBag | undefined {
  const unknownEntries = Object.entries(value).filter(([key]) => !knownKeys.includes(key));

  return unknownEntries.length > 0 ? Object.fromEntries(unknownEntries) : undefined;
}

function normalizeLegacyBag(value: unknown): LegacyFieldBag | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return { ...(value as LegacyFieldBag) };
  }

  return {
    __legacyValue: value,
  };
}

function mergeLegacyBags(
  left?: LegacyFieldBag,
  right?: LegacyFieldBag,
): LegacyFieldBag | undefined {
  if (!left && !right) {
    return undefined;
  }

  return {
    ...(left ?? {}),
    ...(right ?? {}),
  };
}

function toRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function toPersistedUiFilterState(value: unknown): PersistedUiFilterState {
  return toRecord(value) as unknown as PersistedUiFilterState;
}